//to join class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const COLOR_STYLES = {
  // PRIMARY (blue)
  primary: {
    solid:
      'bg-gradient-to-b from-blue-500 to-blue-600 text-white shadow-sm shadow-blue-600/30 ' +
      'hover:from-blue-600 hover:to-blue-700 hover:shadow-blue-700/40 focus-visible:ring-blue-600',
    outline:
      'border border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600 focus-visible:ring-blue-600',
    ghost:
      'text-blue-600 hover:bg-blue-50/80 focus-visible:ring-blue-600',
    link:
      'text-blue-600 underline-offset-4 hover:underline focus-visible:ring-blue-600',
  },
  // NEUTRAL (gray)
  neutral: {
    solid:
      'bg-gradient-to-b from-gray-700 to-gray-900 text-white shadow-sm shadow-gray-900/20 ' +
      'hover:from-gray-800 hover:to-gray-900 focus-visible:ring-gray-700',
    outline:
      'border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus-visible:ring-gray-400',
    ghost:
      'text-gray-700 hover:bg-gray-100/80 focus-visible:ring-gray-400',
    link:
      'text-gray-700 underline-offset-4 hover:underline focus-visible:ring-gray-400',
  },
  // SUCCESS (green)
  success: {
    solid:
      'bg-gradient-to-b from-green-500 to-green-600 text-white shadow-sm shadow-green-600/30 ' +
      'hover:from-green-600 hover:to-green-700 focus-visible:ring-green-600',
    outline:
      'border border-green-500 text-green-600 hover:bg-green-50 hover:border-green-600 focus-visible:ring-green-600',
    ghost:
      'text-green-600 hover:bg-green-50/80 focus-visible:ring-green-600',
    link:
      'text-green-600 underline-offset-4 hover:underline focus-visible:ring-green-600',
  },
  // WARNING (amber)
  warning: {
    solid:
      'bg-gradient-to-b from-amber-400 to-amber-500 text-white shadow-sm shadow-amber-500/30 ' +
      'hover:from-amber-500 hover:to-amber-600 focus-visible:ring-amber-500',
    outline:
      'border border-amber-500 text-amber-600 hover:bg-amber-50 hover:border-amber-600 focus-visible:ring-amber-500',
    ghost:
      'text-amber-600 hover:bg-amber-50/80 focus-visible:ring-amber-500',
    link:
      'text-amber-600 underline-offset-4 hover:underline focus-visible:ring-amber-500',
  },
  // DANGER (red)
  danger: {
    solid:
      'bg-gradient-to-b from-red-500 to-red-600 text-white shadow-sm shadow-red-600/30 ' +
      'hover:from-red-600 hover:to-red-700 focus-visible:ring-red-600',
    outline:
      'border border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600 focus-visible:ring-red-600',
    ghost:
      'text-red-600 hover:bg-red-50/80 focus-visible:ring-red-600',
    link:
      'text-red-600 underline-offset-4 hover:underline focus-visible:ring-red-600',
  },
};


const SIZE_STYLES = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2.5',
};


const ICON_ONLY_STYLES = {
  sm: 'h-8 w-8 p-0',
  md: 'h-10 w-10 p-0',
  lg: 'h-12 w-12 p-0',
};


export default function Button({
  children,
  onClick,
  as,
  href,
  to,
  type = 'button',

  variant = 'solid',
  color = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  style,
  icon,
  endIcon,
  iconOnly = false,
  disabled = false,
  loading = false,
  ariaLabel,
  ...rest
}) {
  const Component = as || 'button';
  const isButton = Component === 'button';
  const isAnchor = Component === 'a';
  const isDisabled = disabled || loading;

  const base =
    'inline-flex items-center justify-center rounded-md font-medium ' +
    'transition-all duration-150 ease-in-out ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'disabled:opacity-55 disabled:cursor-not-allowed disabled:shadow-none ' +
    'active:scale-[0.97] active:brightness-95';

  const hasStyleOverride = style && (style.background || style.backgroundColor || style.color);
  const variantClasses = hasStyleOverride ? '' : (COLOR_STYLES[color]?.[variant] || COLOR_STYLES.primary[variant]);
  const sizeClasses = SIZE_STYLES[size] || SIZE_STYLES.md;

  const iconOnlyClasses = iconOnly ? ICON_ONLY_STYLES[size] : '';
  const widthClasses = fullWidth ? 'w-full' : '';

  const content = (
    <>
      {loading ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{ animation: 'btn-spin 0.7s linear infinite', flexShrink: 0 }}
          aria-hidden="true"
        >
          <style>{`@keyframes btn-spin { to { transform: rotate(360deg); } }`}</style>
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2.5" />
          <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      ) : icon ? (
        <span className={cn(children ? '-ml-0.5' : '')} aria-hidden="true">
          {icon}
        </span>
      ) : null}

      {/* Label */}
      {!iconOnly && children && <span>{children}</span>}

      {/* Trailing icon */}
      {!iconOnly && endIcon ? (
        <span className="-mr-0.5" aria-hidden="true">
          {endIcon}
        </span>
      ) : null}
    </>
  );

  const commonProps = {
    onClick,
    className: cn(base, sizeClasses, variantClasses, widthClasses, iconOnlyClasses, className),
    style,
    'aria-busy': loading || undefined,
    'aria-disabled': isDisabled || undefined,
    ...(iconOnly ? { 'aria-label': ariaLabel } : {}),
    ...rest,
  };

  if (isButton) {
    return (
      <button type={type} disabled={isDisabled} {...commonProps}>
        {content}
      </button>
    );
  }

  if (isAnchor) {
    const rel =
      rest.target === '_blank'
        ? cn(rest.rel || '', 'noopener', 'noreferrer')
        : rest.rel;

    return (
      <a href={href} rel={rel} {...commonProps}>
        {content}
      </a>
    );
  }

  // Custom component (e.g., React Router's Link)
  // Will pass `to` for router, or `href` as fallback.
  const customProps = {};
  if (to != null) customProps.to = to;
  else if (href) customProps.href = href;

  return (
    <Component {...commonProps} {...customProps}>
      {content}
    </Component>
  );
}